import { useEffect, useState } from "react"
import { Paperclip, Search, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { useVerschiebbar } from "@/hooks/useVerschiebbar"
import { VORGANG_OHNE_NAME } from "@/lib/vorgangName"

type Project = { id: number; name: string; status?: string; contact_name?: string; contact_email?: string }

/**
 * "Anhang an HERO-Projekt" Modal — sucht Projekt und lädt Datei hoch.
 * 1:1-Port von ev2AttachHeroModal aus admin/emails.py.
 *
 * Endpoints:
 *  - GET  /api/v1/projects/search?tenant=&q= (Live-HERO-Suche)
 *  - POST /api/v1/emails/{id}/attachments/{att_id}/attach-to-project
 *         { project_match_id, project_name }
 */
export function AttachHeroModal({
  open, onClose, tenantSlug, emailId, anhaenge,
}: {
  open: boolean
  onClose: () => void
  tenantSlug: string
  emailId: number
  /** Ein oder mehrere Anhaenge. Bei fuenf Fotos war es bis zum 26.08.2026
   *  fuenfmal derselbe Weg: Dialog oeffnen, Projekt suchen, hochladen (#42). */
  anhaenge: { id: number; filename: string; size: number }[]
}) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<Project[]>([])
  const [selected, setSelected] = useState<Project | null>(null)
  const [searching, setSearching] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  /** Was nicht durchging — namentlich, damit man weiss, was zu wiederholen ist. */
  const [gescheitert, setGescheitert] = useState<string[]>([])
  const { griff, stil } = useVerschiebbar(open)

  /** Steht der Vorgang aus der Zuordnung der Mail schon fest? Dann zeigt der
   *  Dialog das an, statt „ausgewählt“ ohne Herkunft. */
  const [ausZuordnung, setAusZuordnung] = useState(false)

  useEffect(() => {
    if (open) {
      setQuery(""); setResults([]); setSelected(null); setErr(null)
      setDone(false); setGescheitert([]); setAusZuordnung(false)
    }
  }, [open])

  // #25 (Marlene, 20.10.2026): „Warum muss ich das Projekt suchen, um die
  // Anhänge abzulegen? Die Mail ist schon zugeordnet." Ist die Mail einem
  // Vorgang zugeordnet, steht er vorausgewählt — ein Klick: hochladen. Suchen
  // bleibt möglich. Scheitert das Nachschlagen, bleibt es beim leeren Dialog.
  useEffect(() => {
    if (!open || !emailId) return
    let abgebrochen = false
    ;(async () => {
      try {
        const r = await fetch(`/api/v1/emails/${emailId}`, { credentials: "include" })
        if (!r.ok) return
        const mail = await r.json()
        const pid = Number(mail?.hero_project_id) || 0
        if (!pid) return
        let name = ""
        try {
          const l = await fetch(
            `/api/v1/hero/lookup?tenant=${encodeURIComponent(tenantSlug)}&project_id=${pid}`,
            { credentials: "include" },
          )
          if (l.ok) {
            const d = await l.json()
            const t = (d?.projects || []).find((p: { id: number | string }) => String(p.id) === String(pid))
            name = (t?.name || "").trim()
          }
        } catch { /* Name ist Beiwerk — die Anzeige faellt neutral zurueck */ }
        if (abgebrochen) return
        // Hat der Nutzer in der Zwischenzeit selbst gewaehlt, gewinnt seine Wahl.
        setSelected((s) => s ?? { id: pid, name })
        setAusZuordnung(true)
      } catch { /* kein Vorschlag — Suche wie bisher */ }
    })()
    return () => { abgebrochen = true }
  }, [open, emailId, tenantSlug])

  // Debounce search
  useEffect(() => {
    if (!open) return
    if (query.length < 2) { setResults([]); return }
    const t = setTimeout(async () => {
      setSearching(true); setErr(null)
      try {
        const url = `/api/v1/projects/search?tenant=${encodeURIComponent(tenantSlug)}&q=${encodeURIComponent(query)}`
        const r = await fetch(url, { credentials: "include" })
        const d = await r.json()
        if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`)
        setResults(d.items || [])
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e))
      } finally {
        setSearching(false)
      }
    }, 350)
    return () => clearTimeout(t)
  }, [query, open, tenantSlug])

  /**
   * Laedt alle gewaehlten Anhaenge in denselben Vorgang.
   *
   * 🔴 Nacheinander, nicht gleichzeitig: fuenf Dateien à 2 MB parallel gegen
   * HERO zu werfen ist der sichere Weg in ein Zeitlimit.
   *
   * 🔴 Teilerfolg BLEIBT STEHEN (Entscheidung Paul, 26.08.2026). Gehen drei
   * von fuenf durch, bleiben die drei im Vorgang und die zwei uebrigen werden
   * namentlich gemeldet. Zurueckzurollen hiesse, drei erfolgreiche Uploads
   * wieder zu loeschen und von vorn zu beginnen.
   *
   * 🔴 Und die Meldung sagt NICHT "hochgeladen", wenn nur ein Teil ankam —
   * dieselbe Falle wie bei der Massenaktion in #28.
   */
  const submit = async () => {
    if (!selected || !anhaenge.length) return
    setSubmitting(true); setErr(null); setGescheitert([])
    const misslungen: string[] = []
    for (const a of anhaenge) {
      try {
        const r = await fetch(`/api/v1/emails/${emailId}/attachments/${a.id}/attach-to-project`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            project_match_id: selected.id,
            project_name: selected.name,
          }),
        })
        const d = await r.json().catch(() => ({}))
        if (!r.ok || !d.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`)
      } catch {
        misslungen.push(a.filename)
      }
    }
    setSubmitting(false)
    setGescheitert(misslungen)
    if (misslungen.length) {
      setErr(
        misslungen.length === anhaenge.length
          ? "Kein Anhang konnte hochgeladen werden."
          : `${anhaenge.length - misslungen.length} von ${anhaenge.length} hochgeladen. `
            + `Nicht geklappt hat: ${misslungen.join(", ")}`,
      )
      return
    }
    setDone(true)
    setTimeout(onClose, 1500)
  }

  if (!open) return null

  return (
    <div
      className="pp-overlay"
      data-portal-modal
      style={{
        position: "fixed", inset: 0, zIndex: 1200, background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12, width: 480, maxWidth: "90vw",
          maxHeight: "80vh", overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,.3)",
          display: "flex", flexDirection: "column",
          ...stil,
        }}
      >
        <div {...griff} style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center", ...(griff.style || {}) }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
            <Paperclip className="w-4 h-4" />
            {anhaenge.length === 1 ? "Anhang an HERO-Projekt" : `${anhaenge.length} Anhänge an HERO-Projekt`}
          </h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#666" }}>×</button>
        </div>

        <div style={{ padding: "16px 24px", overflowY: "auto" }}>
          <div style={{
            background: "#f0f7ff", borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13,
          }}>
            {anhaenge.length === 1 ? (
              <><strong>Datei:</strong> {anhaenge[0].filename} ({fmtSize(anhaenge[0].size)})</>
            ) : (
              <>
                <strong>{anhaenge.length} Dateien</strong>{" "}
                ({fmtSize(anhaenge.reduce((s, a) => s + a.size, 0))} gesamt)
                <div style={{ marginTop: 6, fontSize: 12, color: "#475569" }}>
                  {anhaenge.map((a) => (
                    <div key={a.id} style={{
                      // Was durchgefallen ist, wird beim zweiten Anlauf
                      // hervorgehoben — sonst sucht man es in der Liste.
                      color: gescheitert.includes(a.filename) ? "#b91c1c" : undefined,
                      fontWeight: gescheitert.includes(a.filename) ? 600 : undefined,
                    }}>
                      {a.filename} ({fmtSize(a.size)})
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {err && <div className="pp-alert pp-alert-err" style={{ marginBottom: 12 }}>{err}</div>}
          {done && (
            <div className="pp-alert pp-alert-ok" style={{ marginBottom: 12 }}>
              ✓ {anhaenge.length === 1 ? "Anhang" : `${anhaenge.length} Anhänge`} erfolgreich hochgeladen
            </div>
          )}

          {!done && (
            <>
              <label style={{ fontWeight: 600, fontSize: 13, display: "block", marginBottom: 6 }}>
                Projekt suchen
              </label>
              <div style={{ position: "relative", marginBottom: 12 }}>
                <Search className="w-3.5 h-3.5" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setSelected(null); setAusZuordnung(false) }}
                  placeholder="Projektname oder Nummer eingeben..."
                  style={{
                    width: "100%", padding: "10px 12px 10px 34px",
                    border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14, boxSizing: "border-box",
                  }}
                />
              </div>

              <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 16 }}>
                {searching && <div style={{ color: "#9ca3af", fontSize: 13, padding: 8 }}>Suche...</div>}
                {!searching && query.length > 0 && query.length < 2 && (
                  <div style={{ color: "#9ca3af", fontSize: 13, padding: 8 }}>Mindestens 2 Zeichen eingeben</div>
                )}
                {!searching && query.length >= 2 && results.length === 0 && (
                  <div style={{ color: "#9ca3af", fontSize: 13, padding: 8 }}>Keine Projekte gefunden</div>
                )}
                {results.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { setSelected(p); setAusZuordnung(false) }}
                    style={{
                      width: "100%", textAlign: "left", padding: "10px 12px",
                      borderBottom: "1px solid #f3f4f6",
                      cursor: "pointer", background: selected?.id === p.id ? "#f0f7ff" : "#fff",
                      border: "none", borderLeft: selected?.id === p.id ? "3px solid #3c4e7e" : "3px solid transparent",
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      {[p.contact_name, p.contact_email, p.status || "Kein Status", `ID ${p.id}`].filter(Boolean).join(" · ")}
                    </div>
                  </button>
                ))}
              </div>

              {selected && (
                <div
                  data-testid="anhang-vorgang-gewaehlt"
                  style={{
                    background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: 12, marginBottom: 16,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {(selected.name || "").trim() || VORGANG_OHNE_NAME}
                  </div>
                  {ausZuordnung && (
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Der Mail bereits zugeordnet — oben lässt sich ein anderer Vorgang suchen.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {!done && (
          <div style={{
            padding: "16px 24px", borderTop: "1px solid #e5e7eb",
            display: "flex", gap: 10, justifyContent: "flex-end",
          }}>
            <button
              onClick={onClose}
              className="pp-btn pp-btn-sm"
            >Abbrechen</button>
            <button
              onClick={submit}
              disabled={!selected || submitting}
              className={cn("pp-btn pp-btn-sm pp-btn-primary", (!selected || submitting) && "opacity-50 cursor-not-allowed")}
            >
              <X className="w-3 h-3" style={{ display: "none" }} />
              {submitting ? "Wird hochgeladen..." : "Hochladen"}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function fmtSize(bytes: number): string {
  if (!bytes) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
